<template>
  <Teleport
    v-if="shouldRender"
    to="#portal-modal-target"
  >
    <div
      v-show="isOpen"
      :key="name"
      ref="modal"
      :class="['cf-modal-layout', { 'cf-modal-layout--inactive': !isCurrentOpenModal }]"
      :data-name="name"
    >
      <cf-background-overlay
        v-if="isCurrentOpenModal"
        @close-backgound-overlay="closeModalHandler"
      />

      <div
        v-entrap-focus="isVisible"
        :class="['cf-modal-layout__container', {
          'cf-modal-layout__container--compact': modalStyle === 'compact',
          'cf-modal-layout__container--fit-content': modalStyle === 'fit-content',
          'cf-modal-layout__container--hide': !isVisible
        }]"
      >
        <div
          ref="content"
          class="cf-modal"
        >
          <div :class="['cf-modal__header', { 'cf-modal__header--no-border': !displayHeaderTitle }]">
            <div
              class="cf-modal-header__button"
              @click="closeModalHandler"
            >
              <button
                v-if="!isLocked"
                type="button"
                title="Close"
                class="cursor-pointer"
              >
                <span
                  aria-hidden="true"
                  :class="['cf-icon cf-icon--smedium cf-icon--black far', { 'fa-arrow-left': hasBackArrow, 'fa-times': !hasBackArrow }]"
                />

                <span class="ada-text">
                  Close
                </span>
              </button>
            </div>

            <div
              v-if="displayHeaderTitle"
              class="cf-modal-header__title"
            >
              <slot name="title">
                <div id="portal-modal-header-target" />
              </slot>
            </div>
          </div>

          <div :class="['cf-modal__content', { 'cf-modal__content--no-header': !displayHeaderTitle }]">
            <slot :data="injectedData" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfBackgroundOverlay from '../cf-background-overlay/index.vue';


  export default {
    name: 'Modal',

    components: {
      CfBackgroundOverlay
    },

    props: {
      modalStyle: {
        type: String,
        default: null
      },

      name: {
        type: String,
        required: true
      },

      startAtTop: {
        type: Boolean,
        default: false
      },

      closeOnHide: {
        type: Boolean,
        default: false
      },

      displayHeaderTitle: {
        type: Boolean,
        default: true
      },

      hasBackArrow: {
        type: Boolean,
        default: false
      },

      isRequired: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        cachedInjectedData: {},
        contentTouchStart: false,
        modalInputIsFocused: false,
        shouldRender: false
      };
    },

    computed: {
      ...mapGetters('modals', ['currentlyOpenModal', 'modals']),
      ...mapGetters('merchant', ['merchant']),

      injectedData() {
        if (this.modal) {
          return this.modal.data || {};
        }

        return this.cachedInjectedData;
      },

      /**
       * A modal can be designated as "locked" by passing the `is-required` prop to the modal component,
       * or by setting the `isRequired` property in the modal options when opening it.
       */
      isLocked() {
        return this.isRequired || (!!this.modal && this.modal.isRequired);
      },

      isOpen() {
        return !!this.modal;
      },

      isVisible() {
        return !!this.currentlyOpenModal && this.currentlyOpenModal.name === this.name;
      },

      modal() {
        return this.modals.find(modal => modal.name === this.name);
      },

      isCurrentOpenModal() {
        return this.modal?.name === this.currentlyOpenModal?.name;
      }
    },

    watch: {
      modal: {
        handler(modal) {
          if (modal?.data) {
            this.cachedInjectedData = modal.data;
          }
        },
        immediate: true
      },
      isVisible: 'visibilityChangeHandler',
      isOpen: {
        handler: 'isOpenChangeHandler',
        immediate: true
      }
    },

    unmounted() {
      this.destroyedHandler();
    },

    methods: {
      closeModalHandler(forceClose) {
        if ((this.isLocked) && forceClose !== true) {
          return;
        }

        this.$store.dispatch('modals/closeModal', this.name);
      },

      destroyedHandler() {
        /*
          NOTE: This occurs when the <modal> component is removed outside
          of the normal flow. Maybe a <modal> is rendered on a certain page
          and that page is navigated away from while the modal is still open.
        */

        this.$store.dispatch('modals/closeModal', this.name);
      },

      async isOpenChangeHandler(isOpen) {
        if (isOpen) {
          this.shouldRender = true;
          this.$emit('opened', this.injectedData);
        }

        else {
          this.$emit('closing');

          setTimeout(() => {
            // NOTE: Emit closed event once close animation finishes
            this.$emit('closed');

            if (!this.isOpen) {
              this.shouldRender = false;
            }
          }, 300);
        }
      },

      keyUpHandler(event) {
        if (event.keyCode === 27 && this.isVisible) {
          this.closeModalHandler();
        }
      },

      async visibilityChangeHandler(visible) {
        await this.$nextTick();

        if (visible) {
          if (this.startAtTop) {
            this.$refs.content.scrollTo(0, 0);
          }
        }
        else if (this.closeOnHide) {
          this.closeModalHandler();
        }
      }
    }
  };
</script>
